import { AppIcons } from '@backrs_ui/common-ui/designSystem/icons/AppIcon';
import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { FeaturedIcon } from '@backrs_ui/common-ui/src/designSystem/icons/FeaturedIcon';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

type TextDescription = Readonly<{
  text: string;
  bold: boolean;
}>;

type ModalCardProps = Readonly<{
  title: string;
  iconName: AppIcons;
  chipLabel: string;
  chipColor: ColorToken;
  cardDescriptions: readonly TextDescription[];
  leftCardContent: {
    title: string;
    description: readonly TextDescription[];
  };
  rightCardContent: {
    title: string;
    description: readonly TextDescription[];
  };

  /** if true, padding and outside border will be removed */
  isIndividualCard?: boolean;
}>;

export const ROIModalCard = ({
  title,
  iconName,
  chipLabel,
  chipColor,
  cardDescriptions,
  leftCardContent,
  rightCardContent,
  isIndividualCard = false,
}: ModalCardProps) => {
  return (
    <Box sx={cardSx(isIndividualCard)}>
      <Box sx={cardHeaderSx(isIndividualCard)}>
        <Box sx={cardHeaderContentSx}>
          <FeaturedIcon
            iconName={iconName}
            size="lg"
            color="Colors/Base/White"
            backgroundColor={chipColor}
            shape="round"
          />

          <Box sx={textHeaderSx}>
            <Typography variant="text-lg-semibold">{title}</Typography>
            <Chip
              variant="badge"
              size="md"
              type="leading"
              label={chipLabel}
              icon={<Box sx={modalChipIconSx(chipColor)} />}
              sx={modalChipSx}
            />
          </Box>
        </Box>
        <Typography variant="text-sm-regular" component="p">
          {cardDescriptions.map((description) => (
            <Typography
              variant={description.bold ? 'text-sm-bold' : 'text-sm-regular'}
              component="span"
              key={description.text}
            >
              {description.text}
            </Typography>
          ))}
        </Typography>
      </Box>

      <Box sx={cardBodySx(isIndividualCard)}>
        <Box sx={leftCardSx}>
          <Typography variant="text-sm-bold" component="div">
            {leftCardContent.title}
          </Typography>
          <Typography variant="text-sm-regular" component="div">
            {leftCardContent.description.map((description) => (
              <Typography
                variant={description.bold ? 'text-sm-bold' : 'text-sm-regular'}
                component="span"
                key={description.text}
              >
                {description.text}
              </Typography>
            ))}
          </Typography>
        </Box>

        <Box sx={rightCardSx}>
          <Typography variant="text-sm-bold" component="div">
            {rightCardContent.title}
          </Typography>
          <Typography variant="text-sm-regular" component="div">
            {rightCardContent.description.map((description) => (
              <Typography
                variant={description.bold ? 'text-sm-bold' : 'text-sm-regular'}
                component="span"
                key={description.text}
              >
                {description.text}
              </Typography>
            ))}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const cardSx =
  (isIndividualCard: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    borderRadius: backrs.radius['radius-xl'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    zIndex: 100,
    ...(!isIndividualCard && {
      border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    }),
  });

const modalChipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  paddingLeft: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-secondary'],

  '.MuiChip-icon': {
    marginRight: backrs.spacing['spacing-xxs'],
    marginLeft: backrs.spacing['spacing-none'],
  },
});

const modalChipIconSx =
  (color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    width: backrs.spacing['spacing-sm'],
    height: backrs.spacing['spacing-sm'],
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: backrs.colors[color],
  });

const textHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  columnGap: backrs.spacing['spacing-2xl'],
  rowGap: backrs.spacing['spacing-xs'],
  alignItems: {
    xs: 'flex-start',
    sm: 'center',
  },
});

const cardHeaderSx =
  (isIndividualCard: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    p: backrs.spacing['spacing-xl'],
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xs'],
    ...(isIndividualCard && {
      px: backrs.spacing['spacing-none'],
      pt: backrs.spacing['spacing-none'],
    }),
  });

const cardHeaderContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'center',
});

const cardBodySx =
  (isIndividualCard: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    p: backrs.spacing['spacing-xl'],
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    gap: backrs.spacing['spacing-2xl'],
    justifyContent: 'space-evenly',
    ...(isIndividualCard && {
      px: backrs.spacing['spacing-none'],
      pb: backrs.spacing['spacing-none'],
    }),
  });

const rightCardSx: SxProps<Theme> = {
  width: { xs: '100%', sm: '65%' },
};

const leftCardSx: SxProps<Theme> = {
  width: { xs: '100%', sm: '35%' },
};
